import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Plus, Minus } from "lucide-react";

export const Route = createFileRoute("/faq")({
  head: () => ({
    meta: [
      { title: "Knowledge Base & FAQ, HOWDEN" },
      {
        name: "description",
        content:
          "Answers on lab-grown diamonds, certification, the 4Cs, shipping and returns.",
      },
      { property: "og:title", content: "Knowledge Base & FAQ, HOWDEN" },
      {
        property: "og:description",
        content:
          "Answers on lab-grown diamonds, certification, the 4Cs, shipping and returns.",
      },
      { property: "og:url", content: "https://howdendiamonds.com/faq" },
    ],
    links: [{ rel: "canonical", href: "https://howdendiamonds.com/faq" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: SECTIONS.flatMap((s) =>
            s.items.map((it) => ({
              "@type": "Question",
              name: it.q,
              acceptedAnswer: { "@type": "Answer", text: it.a },
            })),
          ),
        }),
      },
    ],
  }),
  component: FaqPage,
});

const SECTIONS = [
  {
    title: "On Lab-Grown Diamonds",
    items: [
      {
        q: "Are lab-grown diamonds real diamonds?",
        a: "Yes. Lab-grown diamonds share the identical chemical composition, crystal structure, and optical properties as mined diamonds. The only meaningful difference is origin.",
      },
      {
        q: "How are they made?",
        a: "Two processes are used: HPHT (High Pressure High Temperature) and CVD (Chemical Vapor Deposition). Both replicate the conditions under which diamonds form naturally, accelerated and refined.",
      },
      {
        q: "Why choose lab-grown over mined?",
        a: "Traceability, value, and conscience. Lab-grown stones come with documented origins and offer significantly more carat per investment, without the environmental and ethical questions of mining.",
      },
    ],
  },
  {
    title: "On the 4Cs",
    items: [
      {
        q: "What are the 4Cs?",
        a: "Cut, Colour, Clarity, and Carat. Cut governs the brilliance; colour describes its tint (D being colourless); clarity records inclusions; carat is the weight. Cut is the only one fully determined by human craft.",
      },
      {
        q: "Which C matters most?",
        a: "Cut. A poorly cut stone of high colour and clarity will always look inferior to a beautifully cut stone of modest grades. We weight our selections accordingly.",
      },
    ],
  },
  {
    title: "Certification",
    items: [
      {
        q: "Who certifies HOWDEN diamonds?",
        a: "Every diamond we set is graded by IGI or GIA. The certificate ships with your piece and the report number is verifiable directly with the laboratory.",
      },
    ],
  },
  {
    title: "Shipping & Returns",
    items: [
      {
        q: "How are pieces shipped?",
        a: "Insured, signature-required, fully tracked. International orders include customs documentation. Delivery typically within 3–7 business days after the piece is finished.",
      },
      {
        q: "Can I return a piece?",
        a: "Yes. Ready-to-wear pieces may be returned within 30 days in original, unworn condition with all packaging and certificates intact. Bespoke commissions and resized pieces are final sale.",
      },
    ],
  },
];

function Item({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-hairline">
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between gap-6 py-6 text-left"
      >
        <span className="font-serif text-xl font-light">{q}</span>
        {open ? (
          <Minus className="h-4 w-4 flex-shrink-0 text-muted-foreground" strokeWidth={1.25} />
        ) : (
          <Plus className="h-4 w-4 flex-shrink-0 text-muted-foreground" strokeWidth={1.25} />
        )}
      </button>
      {open && (
        <p className="pb-8 pr-12 text-sm leading-relaxed text-muted-foreground">{a}</p>
      )}
    </div>
  );
}

function FaqPage() {
  return (
    <div className="mx-auto max-w-4xl px-6 py-20 lg:px-12 lg:py-32">
      <p className="text-xs uppercase tracking-luxe text-muted-foreground">
        Knowledge Base & FAQ
      </p>
      <h1 className="mt-6 font-serif text-5xl font-light lg:text-6xl">
        Considered answers.
      </h1>

      <div className="mt-20 space-y-20">
        {SECTIONS.map((section) => (
          <section key={section.title}>
            <h2 className="mb-2 text-xs uppercase tracking-luxe text-muted-foreground">
              {section.title}
            </h2>
            <div className="border-t border-hairline">
              {section.items.map((item) => (
                <Item key={item.q} {...item} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
